// Pure helpers for custom-model selection and dispatch-target resolution. Originally
// MatterGen-only; generalized (see the "Family-parametric custom-model routing" section
// below) so any generative family (mattergen, fm4m, gt4sd) can register/auto-route/dispatch
// a custom model. A selected custom model always wins over BYOC/direct routing. The org id
// must ride in an `X-Org-Id` header — never a `?orgId=` querystring on the base URL, because
// the runners build `${base}/jobs` (or `/generate`, `/representation`) by string
// concatenation and a querystring on `base` would corrupt that URL.

/** Base URL for one variant of a family: manual/auto modelId wins over BYOC over default. */
export function computeCustomModelBaseUrl({ modelId, proxyBaseUrl, byocActive, byocRelayKey, byocEndpoints, defaultBaseUrl }) {
  if (modelId) return `${proxyBaseUrl}/models/${modelId}`;
  if (byocActive && byocEndpoints?.[byocRelayKey]) return `${proxyBaseUrl}/relay/${byocRelayKey}`;
  return defaultBaseUrl;
}

/** Auth headers for one variant of a family: X-Org-Id rides only on the /models/:id path. */
export function computeCustomModelAuthHeaders({ modelId, orgId, byocActive, byocAuthHeaders }) {
  if (modelId) return { ...(byocAuthHeaders || {}), 'X-Org-Id': orgId };
  return byocActive ? byocAuthHeaders : undefined;
}

/**
 * Headers for fetching a custom model's result files (`${proxyBaseUrl}/models/:id/...`),
 * Part 1a (1.7, B4). The llm-proxy resolves the org of a /models/:modelId/* request from
 * X-Org-Id, so every such fetch needs it, whether the run reached the model through the
 * manual pick or through auto-routing. computeCustomModelAuthHeaders adds it only when it is
 * given a model id, which result fetches do not have. BYOC auth headers ride along when
 * present. Undefined without an org id: no custom model can have run then. Pure.
 */
export function customModelResultHeaders({ orgId, byocAuthHeaders } = {}) {
  if (!orgId) return undefined;
  return { ...(byocAuthHeaders || {}), 'X-Org-Id': orgId };
}

/**
 * Where one row is sent (editable-plan Part 1a, contract 1.5), from the row alone: a custom
 * row goes to its /models/<id> base with X-Org-Id; a row with no custom model goes to the
 * org's BYOC relay for its service when there is one, else to the built-in default. A null
 * customModelId is never auto-routed here: selectCustomModel runs only when rows are built
 * (prefill, the manual adapter), never at dispatch.
 * @param {{ service: string, customModelId: string|null }} row
 * @param {{ orgId, proxyBaseUrl, byocActive, byocEndpoints, byocAuthHeaders,
 *           families: { [service]: { byocRelayKey, defaultBaseUrl } } }} routing
 */
export function rowEndpoint(row, routing) {
  const r = routing || {};
  const fam = (r.families && row && r.families[row.service]) || {};
  const modelId = (row && row.customModelId) || null;
  return {
    baseUrl: computeCustomModelBaseUrl({ modelId, proxyBaseUrl: r.proxyBaseUrl, byocActive: r.byocActive, byocRelayKey: fam.byocRelayKey, byocEndpoints: r.byocEndpoints, defaultBaseUrl: fam.defaultBaseUrl }),
    authHeaders: computeCustomModelAuthHeaders({ modelId, orgId: r.orgId, byocActive: r.byocActive, byocAuthHeaders: r.byocAuthHeaders }),
  };
}

/**
 * The dispatchTarget prepareRows stamps on a row (Part 1a, D4), from the inputs rowEndpoint
 * routes by, so dispatchTargetForBase(rowEndpoint(row).baseUrl) always agrees: a custom row is
 * 'custom-weights' when its model is in weights mode, else 'custom-endpoint' (an unknown model
 * included, so it is never pooled); otherwise 'byoc' when BYOC is active with an endpoint for
 * the row's service, else 'builtin'. Pure, no LLM.
 */
export function dispatchTargetForRow(row, { customModels, byocActive, byocEndpoints } = {}) {
  if (row && row.customModelId) {
    const model = Array.isArray(customModels) ? customModels.find((m) => m && m.id === row.customModelId) : null;
    return model && model.mode === 'weights' ? 'custom-weights' : 'custom-endpoint';
  }
  if (byocActive && byocEndpoints && row && byocEndpoints[row.service]) return 'byoc';
  return 'builtin';
}

/**
 * Deterministically pick a verified custom model of `family`. Conditioning families
 * (mattergen) match on `conditioning === property` (property must be a non-null string —
 * no match without one). Unconditional families (fm4m, gt4sd) family-capture: any
 * verified custom model of that family matches, regardless of `conditioning`. Most-
 * recently-updated wins on ties. Returns a modelId, or null when nothing matches.
 * No LLM — pure equality.
 */
export function selectCustomModel({ customModels, family, property, familyConditions: conditions }) {
  if (!Array.isArray(customModels)) return null;
  if (conditions && !property) return null;
  const ts = (mdl) => (mdl.updatedAt && typeof mdl.updatedAt.seconds === 'number')
    ? mdl.updatedAt.seconds
    : (Number(mdl.updatedAt) || 0);
  const matches = customModels
    .filter((mdl) => mdl && mdl.family === family && mdl.status === 'verified'
      && (conditions ? mdl.conditioning === property : true))
    .sort((a, b) => ts(b) - ts(a));
  return matches.length ? matches[0].id : null;
}

/**
 * Classify the base URL a result was dispatched to, in the Part 1a `dispatchTarget`
 * vocabulary: 'builtin' | 'custom-weights' | 'custom-endpoint' | 'byoc'. It is the
 * inverse of computeCustomModelBaseUrl. Spec 2026-10-07 Part 0.5 uses it as the
 * pooling gate: Privacy Policy section 9 says data processed on customer
 * infrastructure is never pooled, and R4-9 treats endpoint-mode custom models the
 * same way. Classification is by URL path only, so it does not depend on the
 * configured proxy origin. A /models/:id base whose model is not in `customModels`
 * is 'custom-endpoint' (unknown provenance is never pooled). A missing base returns
 * null, which is not poolable either. Pure, no LLM.
 */
export function dispatchTargetForBase(base, customModels) {
  if (typeof base !== 'string' || !base.trim()) return null;
  let path = base;
  try { path = new URL(base).pathname; } catch (_) { /* relative base: classify the raw string */ }
  if (/(^|\/)relay\//.test(path)) return 'byoc';
  const m = path.match(/\/models\/([^/]+)\/?$/);
  if (!m) return 'builtin';
  let id;
  try { id = decodeURIComponent(m[1]); } catch (_) { return 'custom-endpoint'; }
  const model = Array.isArray(customModels) ? customModels.find((x) => x && x.id === id) : null;
  return model && model.mode === 'weights' ? 'custom-weights' : 'custom-endpoint';
}

/** Spec 2026-10-07 Part 0.5 + R4-9: only built-in and weights-mode custom-model results are poolable. */
export function isPoolableDispatchTarget(target) {
  return target === 'builtin' || target === 'custom-weights';
}
